import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  VideoCameraIcon,
  CalendarIcon,
  UserGroupIcon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '../../../components/ui/Modal';
import FormInput from '../../../components/ui/FormInput';
import FileUpload from '../../../components/upload/FileUpload';
import { livestreamService, type Livestream, type CreateLivestreamPayload } from '../../../services/livestreamService';
import { creatorService } from '../../../services/creatorService';

interface LivestreamFormData extends Partial<CreateLivestreamPayload> {
  scheduledStartAt?: string;
  tierIds?: string[];
}

const LivestreamsPage: React.FC = () => {
  const [livestreams, setLivestreams] = useState<Livestream[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLivestream, setSelectedLivestream] = useState<Livestream | null>(null);
  const [formData, setFormData] = useState<LivestreamFormData>({
    title: '',
    description: '',
    scheduledStartAt: '',
    isPublic: true,
    tierIds: [],
    streamUrl: '',
    playbackUrl: '',
  });
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [tiers, setTiers] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadLivestreams();
    loadTiers();
  }, []);

  const loadLivestreams = async () => {
    try {
      setLoading(true);
      const res = await livestreamService.getMyLivestreams();
      setLivestreams(res.livestreams || []);
    } catch (error: any) {
      console.error('Failed to load livestreams:', error);
      alert(error?.response?.data?.message || 'Failed to load livestreams');
    } finally {
      setLoading(false);
    }
  };

  const loadTiers = async () => {
    try {
      const res = await creatorService.getMyCreatorProfile();
      if (res.creatorProfile?.tiers) {
        setTiers(res.creatorProfile.tiers);
      }
    } catch (error) {
      console.error('Failed to load tiers:', error);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: '',
      description: '',
      scheduledStartAt: '',
      isPublic: true,
      tierIds: [],
      streamUrl: '',
      playbackUrl: '',
    });
    setThumbnail(null);
    setThumbnailPreview(null);
    setSelectedLivestream(null);
    setShowCreateModal(true);
  };

  const handleEdit = (livestream: Livestream) => {
    setFormData({
      title: livestream.title,
      description: livestream.description || '',
      scheduledStartAt: livestream.scheduledStartAt
        ? new Date(livestream.scheduledStartAt).toISOString().slice(0, 16)
        : '',
      isPublic: livestream.isPublic,
      tierIds: livestream.tierIds || [],
      streamUrl: livestream.streamUrl || '',
      playbackUrl: livestream.playbackUrl || '',
    });
    setThumbnailPreview(livestream.thumbnailUrl || null);
    setThumbnail(null);
    setSelectedLivestream(livestream);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this livestream?')) return;

    try {
      await livestreamService.deleteLivestream(id);
      await loadLivestreams();
      alert('Livestream deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete livestream:', error);
      alert(error?.response?.data?.message || 'Failed to delete livestream');
    }
  };

  const handleStart = async (id: string) => {
    const streamUrl = prompt('Enter stream URL (RTMP/HLS):');
    const playbackUrl = prompt('Enter playback URL (optional):');

    if (!streamUrl) {
      alert('Stream URL is required');
      return;
    }

    try {
      await livestreamService.startLivestream(id, streamUrl, playbackUrl || undefined);
      await loadLivestreams();
      alert('Livestream started');
    } catch (error: any) {
      console.error('Failed to start livestream:', error);
      alert(error?.response?.data?.message || 'Failed to start livestream');
    }
  };

  const handleEnd = async (id: string) => {
    if (!confirm('Are you sure you want to end this livestream?')) return;

    try {
      await livestreamService.endLivestream(id);
      await loadLivestreams();
      alert('Livestream ended');
    } catch (error: any) {
      console.error('Failed to end livestream:', error);
      alert(error?.response?.data?.message || 'Failed to end livestream');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      alert('Title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateLivestreamPayload = {
        title: formData.title!,
        description: formData.description,
        scheduledStartAt: formData.scheduledStartAt
          ? new Date(formData.scheduledStartAt).toISOString()
          : undefined,
        isPublic: formData.isPublic ?? true,
        tierIds: formData.tierIds || [],
        streamUrl: formData.streamUrl || undefined,
        playbackUrl: formData.playbackUrl || undefined,
      };

      if (selectedLivestream) {
        await livestreamService.updateLivestream(
          selectedLivestream.id,
          payload,
          thumbnail || undefined
        );
        alert('Livestream updated successfully');
      } else {
        await livestreamService.createLivestream(payload, thumbnail || undefined);
        alert('Livestream created successfully');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      await loadLivestreams();
    } catch (error: any) {
      console.error('Failed to save livestream:', error);
      alert(error?.response?.data?.message || 'Failed to save livestream');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatStatus = (status: string) => {
    const statusColors: Record<string, string> = {
      LIVE: 'bg-red-100 text-red-700',
      SCHEDULED: 'bg-blue-100 text-blue-700',
      ENDED: 'bg-gray-100 text-gray-700',
      CANCELLED: 'bg-gray-100 text-gray-700',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatScheduledTime = (dateString?: string) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Livestreams</h1>
          <p className="text-gray-600 mt-1">Manage your livestreams</p>
        </div>
        <Button onClick={handleCreate} leftIcon={<PlusIcon className="w-5 h-5" />}>
          Create Livestream
        </Button>
      </div>

      {/* Livestreams List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400">Loading livestreams...</div>
        </div>
      ) : livestreams.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <VideoCameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No livestreams yet</h3>
            <p className="text-gray-600 mb-4">Create your first livestream to get started</p>
            <Button onClick={handleCreate} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Create Livestream
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {livestreams.map((livestream) => (
            <Card key={livestream.id} className="overflow-hidden">
              {/* Thumbnail */}
              <div className="relative aspect-video bg-gray-900">
                {livestream.thumbnailUrl ? (
                  <img
                    src={livestream.thumbnailUrl}
                    alt={livestream.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-400">
                    <VideoCameraIcon className="w-12 h-12" />
                  </div>
                )}
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${formatStatus(livestream.status)}`}>
                    {livestream.status}
                  </span>
                </div>
                {/* Viewer Count */}
                {livestream.status === 'LIVE' && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-full text-xs">
                    <UserGroupIcon className="w-3 h-3" />
                    <span>{livestream.viewerCount.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {livestream.title}
                </h3>
                {livestream.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {livestream.description}
                  </p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  {livestream.scheduledStartAt && (
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      <span>{formatScheduledTime(livestream.scheduledStartAt)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {livestream.status === 'SCHEDULED' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStart(livestream.id)}
                      leftIcon={<PlayIcon className="w-4 h-4" />}
                    >
                      Start
                    </Button>
                  )}
                  {livestream.status === 'LIVE' && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleEnd(livestream.id)}
                      leftIcon={<StopIcon className="w-4 h-4" />}
                    >
                      End
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(livestream)}
                    leftIcon={<PencilIcon className="w-4 h-4" />}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(livestream.id)}
                    leftIcon={<TrashIcon className="w-4 h-4" />}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <Modal
          isOpen={showCreateModal || showEditModal}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
          }}
        >
          <ModalHeader>
            {selectedLivestream ? 'Edit Livestream' : 'Create Livestream'}
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <FormInput
                label="Title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter livestream title"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter livestream description"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                />
              </div>

              <FormInput
                label="Scheduled Start Time"
                type="datetime-local"
                value={formData.scheduledStartAt || ''}
                onChange={(e) => setFormData({ ...formData, scheduledStartAt: e.target.value })}
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={formData.isPublic ?? true}
                  onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  className="w-4 h-4 text-patreon-orange rounded focus:ring-patreon-orange"
                />
                <label htmlFor="isPublic" className="text-sm text-gray-700">
                  Public (visible to all)
                </label>
              </div>

              {!formData.isPublic && tiers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tier Access
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {tiers.map((tier) => (
                      <label key={tier.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.tierIds?.includes(tier.id) || false}
                          onChange={(e) => {
                            const tierIds = formData.tierIds || [];
                            if (e.target.checked) {
                              setFormData({ ...formData, tierIds: [...tierIds, tier.id] });
                            } else {
                              setFormData({
                                ...formData,
                                tierIds: tierIds.filter((id) => id !== tier.id),
                              });
                            }
                          }}
                          className="w-4 h-4 text-patreon-orange rounded focus:ring-patreon-orange"
                        />
                        <span className="text-sm text-gray-700">{tier.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <FormInput
                label="Stream URL (RTMP/HLS)"
                value={formData.streamUrl || ''}
                onChange={(e) => setFormData({ ...formData, streamUrl: e.target.value })}
                placeholder="rtmp://..."
              />

              <FormInput
                label="Playback URL (optional)"
                value={formData.playbackUrl || ''}
                onChange={(e) => setFormData({ ...formData, playbackUrl: e.target.value })}
                placeholder="https://..."
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thumbnail
                </label>
                <FileUpload
                  accept="image/*"
                  multiple={false}
                  maxSize={5}
                    onUpload={async (files) => {
                    if (files[0]) {
                      setThumbnail(files[0]);
                      setThumbnailPreview(URL.createObjectURL(files[0]));
                    }
                  }}
                    value={[]}
                />
                {(thumbnailPreview || selectedLivestream?.thumbnailUrl) && (
                  <img
                    src={thumbnailPreview || selectedLivestream?.thumbnailUrl || ''}
                    alt="Thumbnail preview"
                    className="mt-2 w-32 h-20 object-cover rounded-lg"
                  />
                )}
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              {selectedLivestream ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default LivestreamsPage;
